'use client';

import { useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Upload, AlertCircle, CheckCircle2, Download } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { UploadProgress } from '@/components/upload-progress';
import { useUploadProgress } from '@/hooks/use-upload-progress';
import { cn } from '@/lib/utils';
import { useImportUsersMutation } from '../services/users.mutations';
import { usersApi } from '../services/users.api';
import type { BulkImportResult } from '../types';

interface UsersImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UsersImportDialog({
  open,
  onOpenChange
}: UsersImportDialogProps) {
  const t = useTranslations('users');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const importMutation = useImportUsersMutation();
  const { progress, onProgress, reset } = useUploadProgress();

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await usersApi.downloadTemplate();
    } catch {
      toast.error(t('downloadTemplateError'));
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setResult(null);
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    try {
      const data = await importMutation.mutateAsync({
        file: selectedFile,
        onProgress
      });
      setResult(data);
    } catch {
      // error handled via meta.suppressErrorToast — show inline
    } finally {
      reset();
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setSelectedFile(null);
      setResult(null);
      reset();
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>{t('importFromExcel')}</DialogTitle>
        </DialogHeader>

        <div className='space-y-4'>
          {/* File picker */}
          <div
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors',
              isDragging
                ? 'border-primary bg-primary/10'
                : selectedFile
                  ? 'border-primary/40 bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-primary/40'
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <Upload className='text-muted-foreground mb-2 h-8 w-8' />
            {selectedFile ? (
              <p className='text-sm font-medium'>{selectedFile.name}</p>
            ) : (
              <>
                <p className='text-sm font-medium'>
                  {isDragging ? t('dropFileHere') : t('clickOrDragToUpload')}
                </p>
                <p className='text-muted-foreground text-xs'>
                  {t('acceptedFormats')}
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type='file'
              accept='.xlsx,.xls'
              className='hidden'
              onChange={handleFileChange}
            />
          </div>

          <UploadProgress value={progress} />

          {/* Results */}
          {result && (
            <div className='space-y-3'>
              <div className='flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 dark:border-green-800 dark:bg-green-950'>
                <CheckCircle2 className='h-5 w-5 shrink-0 text-green-600' />
                <span className='text-sm font-medium text-green-700 dark:text-green-300'>
                  {t('importedCount', { count: result.imported })}
                </span>
              </div>

              {result.failed.length > 0 && (
                <div className='rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950'>
                  <div className='mb-2 flex items-center gap-2'>
                    <AlertCircle className='h-4 w-4 shrink-0 text-red-600' />
                    <span className='text-sm font-medium text-red-700 dark:text-red-300'>
                      {t('failedCount', { count: result.failed.length })}
                    </span>
                  </div>
                  <div className='max-h-48 space-y-1.5 overflow-y-auto'>
                    {result.failed.map((row) => (
                      <div
                        key={row.row}
                        className='rounded border border-red-200 bg-white p-2 text-xs dark:border-red-800 dark:bg-red-900/30'
                      >
                        <span className='font-medium text-red-700 dark:text-red-300'>
                          {t('rowNumber', { row: row.row })}
                          {row.email ? ` (${row.email})` : ''}
                        </span>
                        <ul className='mt-1 list-inside list-disc text-red-600 dark:text-red-400'>
                          {row.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className='flex-col gap-2 sm:flex-row sm:justify-between'>
          <Button
            variant='ghost'
            size='sm'
            disabled={isDownloading}
            onClick={handleDownloadTemplate}
          >
            <Download className='me-2 h-4 w-4' />
            {isDownloading ? t('downloading') : t('downloadTemplate')}
          </Button>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={() => handleClose(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!selectedFile || importMutation.isPending}
            >
              {importMutation.isPending ? t('importing') : t('import')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
